import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Download, Plus, FileText, FileSpreadsheet, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { usePermission } from '@/components/guards/permission-guard';
import { dateStamp } from '@/lib/download-xlsx';
import { downloadMovementsPdf, downloadMovementsXlsx } from '@/lib/cari-movements-export';
import { TRAVEL_COMPANY } from '@/lib/report-company';
import type { MovementDoc } from '@/lib/cari-movements-doc';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { TravelAddMovementDialog } from '@/components/travel/cari/add-movement-dialog';
import {
  selectClass,
  useTravelCariTypes,
  useCariTypeLabel,
  CURRENCIES,
  CURRENCY_NAMES,
  fmtMoney,
  useFiscalYear,
} from '../shared';

// Global movement log across every travel cari (imported subsidiary ledger).
export function TravelCariTransactionsPage() {
  const availableTypes = useTravelCariTypes();
  const typeLabel = useCariTypeLabel();
  const { t, i18n } = useTranslation('accounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const { years, fiscalYear, setFiscalYear } = useFiscalYear();
  const canCreate = usePermission('ACCOUNTING', 'CREATE');
  const canExport = usePermission('ACCOUNTING', 'EXPORT', 'TRAVEL');

  const [debounced, setDebounced] = useSessionState<string>(
    'travel:cari-transactions:debounced',
    ''
  );
  const [search, setSearch] = useState(debounced);
  const [addOpen, setAddOpen] = useState(false);
  const [cariType, setCariType] = useSessionState<string>('travel:cari-transactions:cariType', '');
  const [currency, setCurrency] = useSessionState<string>('travel:cari-transactions:currency', '');
  const [dateFrom, setDateFrom] = useSessionState<string>('travel:cari-transactions:dateFrom', '');
  const [dateTo, setDateTo] = useSessionState<string>('travel:cari-transactions:dateTo', '');
  const [page, setPage] = useState(1);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const utils = trpc.useUtils();

  const onSearch = (v: string) => {
    setSearch(v);
    setTimeout(() => {
      setDebounced(v);
      setPage(1);
    }, 300);
  };

  const filters = {
    fiscalYear: fiscalYear ?? undefined,
    search: debounced || undefined,
    cariType: (cariType || undefined) as (typeof availableTypes)[number] | undefined,
    currency: (currency || undefined) as (typeof CURRENCIES)[number] | undefined,
    dateFrom: dateFrom ? new Date(dateFrom) : undefined,
    dateTo: dateTo ? new Date(dateTo) : undefined,
  };

  const buildDoc = (
    res: Awaited<ReturnType<typeof utils.travelCari.exportAllMovements.fetch>>
  ): MovementDoc => {
    const fmtDay = (val: string) => new Date(val).toLocaleDateString(dateLocale);
    const periodParts = [
      fiscalYear ? String(fiscalYear) : '',
      dateFrom || dateTo
        ? `${dateFrom ? fmtDay(dateFrom) : '…'} – ${dateTo ? fmtDay(dateTo) : '…'}`
        : '',
    ].filter(Boolean);
    return {
      company: TRAVEL_COMPANY,
      meta: {
        mode: 'list',
        title: t('cari.transactionsTitle', 'Cari Movements').toLocaleUpperCase(
          i18n.language === 'tr' ? 'tr-TR' : 'en-US'
        ),
        periodLabel: periodParts.join('  ·  ') || '—',
        generatedLabel: t('cari.exportGenerated', 'Generated {{ts}}', {
          ts: new Date().toLocaleString(dateLocale),
        }),
        params: [
          ...(fiscalYear
            ? [{ label: t('cari.fiscalYear', 'Fiscal Year'), value: String(fiscalYear) }]
            : []),
          { label: t('cari.currency', 'Currency'), value: currency || t('cari.all', 'All') },
          { label: t('cari.type', 'Type'), value: cariType || t('cari.all', 'All') },
          ...(debounced ? [{ label: t('cari.search', 'Search'), value: debounced }] : []),
        ],
        columns: {
          no: '#',
          date: t('cari.date', 'Date'),
          cari: t('cari.cari', 'Cari'),
          type: t('cari.type', 'Type'),
          code: t('cari.cariNo', 'Cari No'),
          voucher: t('cari.voucherNo', 'Voucher No'),
          description: t('cari.description', 'Description'),
          currency: t('cari.currency', 'Currency'),
          fxDebit: t('cari.fxDebit', 'FX Debit'),
          fxCredit: t('cari.fxCredit', 'FX Credit'),
          tryDebit: t('cari.tryDebit', 'TL Debit'),
          tryCredit: t('cari.tryCredit', 'TL Credit'),
          balance: t('cari.balance', 'Balance'),
        },
        totalLabel: t('cari.total', 'TOTAL'),
        balanceLabel: t('cari.balance', 'BALANCE'),
        debitSide: t('cari.debitSide', '(B)'),
        creditSide: t('cari.creditSide', '(A)'),
        manualTag: 'M',
        emptyLabel: t('cari.noMovements', 'No movements found'),
        pageLabel: (p, tt) =>
          t('cari.exportPage', 'Page {{page}} / {{total}}', { page: p, total: tt }),
        currencyNames: CURRENCY_NAMES,
      },
      data: { rows: res.rows, totalsByCurrency: res.totalsByCurrency },
    };
  };

  const runExport = async (kind: 'pdf' | 'xlsx') => {
    setExporting(true);
    try {
      const res = await utils.travelCari.exportAllMovements.fetch(filters);
      const doc = buildDoc(res);
      const base = `travel-cari-hareketleri-${dateStamp()}`;
      if (kind === 'pdf') await downloadMovementsPdf(doc, `${base}.pdf`);
      else await downloadMovementsXlsx(doc, `${base}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading } = trpc.travelCari.allMovements.useQuery({ ...filters, page });

  const items = data?.items ?? [];
  const pag = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('cari.transactionsTitle', 'Cari Movements')}</h1>
          <p className="text-muted-foreground">
            {t('cari.transactionsSubtitle', 'All current-account movements across every cari')}
            {pag ? ` (${pag.total})` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={exporting || !items.length}>
                  {exporting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {t('cari.export', 'Export')}
                  <ChevronDown className="ml-2 h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => runExport('pdf')}>
                  <FileText className="h-4 w-4 text-red-600" />
                  {t('cari.exportPdf', 'PDF')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => runExport('xlsx')}>
                  <FileSpreadsheet className="h-4 w-4 text-green-600" />
                  {t('cari.exportExcel', 'Excel')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreate && (
            <Button variant="outline" onClick={() => setPurchaseOpen(true)}>
              {t('cari.addPurchaseInvoice')}
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('cari.addMovement', 'Add Movement')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.fiscalYear', 'Fiscal Year')}</Label>
          <select
            value={fiscalYear ?? ''}
            onChange={(e) => {
              setFiscalYear(Number(e.target.value));
              setPage(1);
            }}
            className={`${selectClass} block w-24`}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">{t('cari.search', 'Search')}</Label>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t(
              'cari.searchMovementsPlaceholder',
              'Name, description, voucher, code...'
            )}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 mt-1 h-8 w-72 border px-3 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.type', 'Type')}</Label>
          <select
            value={cariType}
            onChange={(e) => {
              setCariType(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} block w-28`}
          >
            <option value="">{t('cari.all', 'All')}</option>
            {availableTypes.map((ct) => (
              <option key={ct} value={ct}>
                {typeLabel(ct)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.currency', 'Currency')}</Label>
          <select
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} block w-24`}
          >
            <option value="">{t('cari.all', 'All')}</option>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.dateFrom', 'From')}</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="w-36"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.dateTo', 'To')}</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="w-36"
          />
        </div>
        {(search || cariType || currency || dateFrom || dateTo) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch('');
              setDebounced('');
              setCariType('');
              setCurrency('');
              setDateFrom('');
              setDateTo('');
              setPage(1);
            }}
          >
            {t('cari.clear', 'Clear')}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="px-3 py-3 text-left font-medium">{t('cari.date', 'Date')}</th>
              <th className="px-3 py-3 text-left font-medium">{t('cari.cari', 'Cari')}</th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cari.voucherNo', 'Voucher No')}
              </th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cari.description', 'Description')}
              </th>
              <th className="px-3 py-3 text-center font-medium">
                {t('cari.currency', 'Currency')}
              </th>
              <th className="px-3 py-3 text-right font-medium">{t('cari.fxDebit', 'FX Debit')}</th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cari.fxCredit', 'FX Credit')}
              </th>
              <th className="px-3 py-3 text-right font-medium">{t('cari.tryDebit', 'TL Debit')}</th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cari.tryCredit', 'TL Credit')}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="text-muted-foreground py-8 text-center">
                  {t('cari.noMovements', 'No movements found')}
                </td>
              </tr>
            )}
            {items.map((m) => (
              <tr
                key={m.id}
                className="hover:bg-muted/30 cursor-pointer border-b last:border-0"
                onClick={() =>
                  navigate(`/travel/cari/${m.cariCode}/movements?year=${m.fiscalYear}`)
                }
              >
                <td className="whitespace-nowrap px-3 py-2.5">
                  {new Date(m.date).toLocaleDateString('tr-TR')}
                </td>
                <td className="px-3 py-2.5">
                  <span className="font-medium">{m.counterpartyName}</span>
                  <span className="bg-muted text-muted-foreground ml-1.5 rounded px-1.5 py-0.5 text-[10px]">
                    {m.cariType}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs">
                  {m.voucherNo}
                  {m.isManual && (
                    <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
                      M
                    </span>
                  )}
                  {m.isOpeningCarryForward && (
                    <span className="ml-1.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700">
                      {t('cari.openingShort', 'DEVIR')}
                    </span>
                  )}
                </td>
                <td className="max-w-[280px] px-3 py-2.5">
                  <span className="block truncate">{m.description}</span>
                </td>
                <td className="px-3 py-2.5 text-center">{m.currency}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.fxDebit > 0 ? fmtMoney(m.fxDebit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.fxCredit > 0 ? fmtMoney(m.fxCredit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.tryDebit > 0 ? fmtMoney(m.tryDebit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.tryCredit > 0 ? fmtMoney(m.tryCredit) : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pag && pag.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            {(page - 1) * pag.pageSize + 1}-{Math.min(page * pag.pageSize, pag.total)} / {pag.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('cari.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pag.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('cari.next', 'Next')}
            </Button>
          </div>
        </div>
      )}

      <TravelAddMovementDialog purchase open={purchaseOpen} onOpenChange={setPurchaseOpen} />
      <TravelAddMovementDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
